import { useClinicDay } from '@hooks/useClinicDay'
import { useAppSelector } from '@store/hooks'
import React, { useEffect, useMemo, useState, ReactNode } from 'react'
import './CallRecordingStyle.css'
import useCallRecording from './useCallRecording'
import SettingsSkeleton from '@pages/Settings/SettingsSkeleton.page'
import useCommunicationsLeaveGuard from '../shared/useCommunicationsLeaveGuard'
import {
  CallRecordingActionsCell,
  CallRecordingAudioCell,
  CallRecordingTypeCell,
} from './CallRecording.cells'
import { ICalls } from '@dataTypes/calls.type'
import {
  formatPhoneNumber,
  normalizePhoneNumber,
} from '@utils/numberFormatter.util'
import { formatCallDate, getCallTimestamp } from '@utils/call.util'
import {
  Button,
  DataTable,
  EmptyState,
  InputWithUnit,
  Modal,
  SearchWithFilter,
  ToggleSwitch,
  type DataTableColumn,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import { Icon as Delete2Icon } from '@enamelai/design-system/icons/Delete2'
import { Icon as InfoIcon } from '@enamelai/design-system/icons/Info'
import { Icon as RecordIcon } from '@enamelai/design-system/icons/Record'

type RecordingTableRow = { id: string }

type RecordingSortColumn =
  | 'phone_number'
  | 'datetime'
  | 'call_type'
  | 'audio_url'
  | 'handled_by'

const DEFAULT_PAGE_SIZE = 10

const RETENTION_UNIT_OPTIONS = [
  'day/s',
  'week/s',
  'month/s',
  'year/s',
  'No expiration',
].map((unit) => ({ label: unit, value: unit }))

const textSortValue = (value?: string | null): string | null => {
  const trimmed = value?.trim()
  return trimmed ? trimmed.toLowerCase() : null
}

const compareSortValues = (
  aValue: string | number | null,
  bValue: string | number | null,
  direction: DataTableSortDirection,
): number => {
  const multiplier = direction === 'asc' ? 1 : -1
  if (aValue === bValue) return 0
  if (aValue === null) return 1
  if (bValue === null) return -1
  return aValue < bValue ? -multiplier : multiplier
}

const sortRows = <T,>(
  rows: T[],
  active: boolean,
  direction: DataTableSortDirection,
  getValue: (row: T) => string | number | null,
): T[] => {
  if (!active) return rows
  return [...rows].sort((left, right) =>
    compareSortValues(getValue(left), getValue(right), direction),
  )
}

const recordingPhone = (call: ICalls): string =>
  call.call_type === 'Incoming'
    ? formatPhoneNumber(call.caller_number, false)
    : formatPhoneNumber(call.receiver_number, false)

const recordingTypeLabel = (call: ICalls): string =>
  ['Incoming', 'Missed'].includes(call.call_type) ? 'Inbound' : 'Outbound'

const getRecordingSortValue = (
  call: ICalls,
  column: RecordingSortColumn,
  staffLabel: string,
): string | number | null => {
  switch (column) {
    case 'phone_number':
      return textSortValue(normalizePhoneNumber(recordingPhone(call)))
    case 'datetime': {
      return getCallTimestamp(call.start_date_time)
    }
    case 'call_type':
      return textSortValue(recordingTypeLabel(call))
    case 'audio_url':
      return Number.isFinite(call.duration) ? call.duration : null
    case 'handled_by':
      return textSortValue(staffLabel)
  }
}

const useSortedPage = <T extends string>(): {
  currentPage: number
  pageSize: number
  sortColumnId: T | null
  sortDirection: DataTableSortDirection
  handleSort: (columnId: string) => void
  setCurrentPage: (page: number) => void
  onPageSizeChange: (size: number) => void
} => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sortColumnId, setSortColumnId] = useState<T | null>(null)
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')

  const handleSort = (columnId: string): void => {
    const column = columnId as T
    if (sortColumnId !== column) {
      setSortColumnId(column)
      setSortDirection('asc')
    } else if (sortDirection === 'asc') {
      setSortDirection('desc')
    } else {
      setSortColumnId(null)
    }
    setCurrentPage(1)
  }

  const onPageSizeChange = (size: number): void => {
    setPageSize(size)
    setCurrentPage(1)
  }

  return {
    currentPage,
    pageSize,
    sortColumnId,
    sortDirection,
    handleSort,
    setCurrentPage,
    onPageSizeChange,
  }
}

const CallRecording = (): React.ReactElement => {
  const hooks = useCallRecording()
  const clinicTimeZone = useAppSelector(
    (state) => state.clinic.selectedClinic?.time_zone || 'UTC',
  )
  useClinicDay(clinicTimeZone)
  const leaveGuard = useCommunicationsLeaveGuard(
    hooks.hasChanges && hooks.dataReady,
  )
  const { staffDisplay } = hooks
  const hasSearchOrFilter =
    hooks.mainSearchQuery.trim().length > 0 ||
    (hooks.mainFilters.type?.length ?? 0) > 0
  const recordingsEmpty = hooks.recordings.length === 0
  const isTrulyEmpty = Boolean(
    hooks.dataReady && recordingsEmpty && !hasSearchOrFilter,
  )
  const {
    currentPage,
    pageSize,
    sortColumnId,
    sortDirection,
    handleSort,
    setCurrentPage,
    onPageSizeChange,
  } = useSortedPage<RecordingSortColumn>()

  const recordingEntries = useMemo(
    () => hooks.recordings.map((call) => ({ id: call.call_id, call })),
    [hooks.recordings],
  )
  const recordingsById = new Map(
    recordingEntries.map((entry) => [entry.id, entry.call]),
  )
  const renderRecording =
    (render: (call: ICalls) => ReactNode) =>
    (row: RecordingTableRow): ReactNode => {
      const call = recordingsById.get(row.id)
      return call ? render(call) : null
    }

  useEffect(() => {
    setCurrentPage(1)
  }, [hooks.mainSearchQuery, hooks.mainFilters, setCurrentPage])

  useEffect(() => {
    hooks.setHeaderButtons(
      <>
        <Button
          size="sm"
          type="button"
          variant="secondary"
          disabled={!hooks.hasChanges || hooks.loading || !hooks.dataReady}
          onClick={() => leaveGuard.requestLeave(hooks.handleReset)}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          type="button"
          variant="primary"
          disabled={!hooks.hasChanges || hooks.loading || !hooks.dataReady}
          onClick={() => {
            void hooks.handleSave()
          }}
        >
          Save changes
        </Button>
      </>,
    )

    return () => {
      hooks.setHeaderButtons(null)
    }
  }, [
    hooks.dataReady,
    hooks.handleReset,
    hooks.handleSave,
    hooks.hasChanges,
    hooks.loading,
    hooks.setHeaderButtons,
    leaveGuard.requestLeave,
  ])

  const columns: DataTableColumn<RecordingTableRow>[] = [
    {
      id: 'phone_number',
      label: 'Phone number',
      width: '20%',
      sortable: true,
      renderCell: renderRecording((call) => recordingPhone(call)),
    },
    {
      id: 'datetime',
      label: 'Date & time',
      width: '16%',
      sortable: true,
      renderCell: renderRecording((call) =>
        formatCallDate(call.start_date_time, clinicTimeZone),
      ),
    },
    {
      id: 'call_type',
      label: 'Type',
      width: '12%',
      sortable: true,
      renderCell: renderRecording((call) => (
        <CallRecordingTypeCell {...call} />
      )),
    },
    {
      id: 'audio_url',
      label: 'Recorded audio',
      width: '28%',
      sortable: true,
      renderCell: renderRecording((call) => (
        <CallRecordingAudioCell call={call} />
      )),
    },
    {
      id: 'handled_by',
      label: 'Staff',
      width: '16%',
      sortable: true,
      renderCell: renderRecording((call) => staffDisplay(call)),
    },
  ]

  const sortedRecordings = useMemo(
    () =>
      sortRows(
        recordingEntries,
        sortColumnId !== null,
        sortDirection,
        (entry) =>
          sortColumnId
            ? getRecordingSortValue(
                entry.call,
                sortColumnId,
                staffDisplay(entry.call),
              )
            : null,
      ),
    [recordingEntries, sortColumnId, sortDirection, staffDisplay],
  )
  const totalPages = Math.max(1, Math.ceil(sortedRecordings.length / pageSize))
  const pageNumber = Math.min(currentPage, totalPages)
  const pageRows = useMemo(
    (): RecordingTableRow[] =>
      sortedRecordings
        .slice((pageNumber - 1) * pageSize, pageNumber * pageSize)
        .map((entry) => ({ id: entry.id })),
    [sortedRecordings, pageNumber, pageSize],
  )

  if (!hooks.dataReady) {
    return (
      <div
        className="scp-call-recording-container"
        id="settings-call-recording"
      >
        {leaveGuard.modal}
        <SettingsSkeleton variant="table" pageKey="comm-call-record" />
      </div>
    )
  }

  return (
    <div className="scp-call-recording-container" id="settings-call-recording">
      {leaveGuard.modal}
      <div className="scp-filter-container">
        Recording scope
        <div className="scp-filters">
          <div className="scp-filter-item">
            Record inbound calls
            <ToggleSwitch
              id="inbound-calls"
              checked={hooks.data.inbound_calls}
              aria-label="Record inbound calls"
              onChange={(checked) => {
                hooks.handleChange('inbound_calls', checked)
              }}
            />
          </div>
          <div className="scp-filter-item">
            Record outbound calls
            <ToggleSwitch
              id="outbound-calls"
              checked={hooks.data.outbound_calls}
              aria-label="Record outbound calls"
              onChange={(checked) => {
                hooks.handleChange('outbound_calls', checked)
              }}
            />
          </div>
          <div className="scp-filter-item">
            Record internal calls
            <ToggleSwitch
              id="internal-calls"
              checked={false}
              disabled
              aria-label="Record internal calls"
            />
          </div>
          <div className="scp-filter-item">
            Auto-start recording
            <ToggleSwitch
              id="auto-start-recording"
              checked={hooks.data.auto_start}
              aria-label="Auto-start recording"
              onChange={(checked) => {
                hooks.handleChange('auto_start', checked)
              }}
            />
          </div>
          <div className="scp-filter-item">
            Play tone when recording starts
            <ToggleSwitch
              id="play-tone-recording"
              checked={hooks.data.play_tone}
              aria-label="Play tone when recording starts"
              onChange={(checked) => {
                hooks.handleChange('play_tone', checked)
              }}
            />
          </div>
          <div className="scp-filter-item">
            Pause recording on hold
            <ToggleSwitch
              id="pause-recording-on-hold"
              checked={hooks.data.pause_recording_on_hold}
              aria-label="Pause recording on hold"
              onChange={(checked) => {
                hooks.handleChange('pause_recording_on_hold', checked)
              }}
            />
          </div>
        </div>
      </div>
      <div className="scp-filter-container">
        Retention behaviour
        <div className="scp-filters">
          <InputWithUnit
            label="Retention period"
            placeholder="-"
            value={(hooks.data.retention_period || '').toString()}
            onChange={(value) => {
              if (hooks.data.retention_period_unit === 'No expiration') return
              hooks.handleChange('retention_period', Number(value))
            }}
            unit={hooks.data.retention_period_unit}
            units={RETENTION_UNIT_OPTIONS}
            onUnitChange={(unit) => {
              const next = typeof unit === 'string' ? unit : 'year/s'
              hooks.handleChange('retention_period_unit', next)
              if (next === 'No expiration') {
                hooks.handleChange('retention_period', 0)
              }
            }}
          />
          <div className="scp-filter-item">
            Encrypt recording (AES-256)
            <ToggleSwitch
              id="encrypt-recording"
              checked={hooks.data.encryption}
              aria-label="Encrypt recording (AES-256)"
              onChange={(checked) => {
                hooks.handleChange('encryption', checked)
              }}
            />
          </div>
        </div>
      </div>
      <div className="scp-table-group">
        <div className="scp-table-title-container">
          <span className="scp-table-title">
            Recent recordings
            <button
              type="button"
              className="scp-info-button ds-section-card__icon-button"
              aria-label="Recent recordings information"
              title="Call recordings in the last 5 days. Other call recordings can be found in the Call Logs."
            >
              <InfoIcon aria-hidden="true" />
            </button>
          </span>
          {!isTrulyEmpty ? (
            <div className="component-buttons">
              <SearchWithFilter
                placeholder="Search"
                value={hooks.mainSearchQuery}
                onChange={hooks.setMainSearchQuery}
                filters={{
                  type: ['Inbound', 'Outbound'],
                }}
                filterLabels={{ type: 'Type' }}
                selectedFilters={hooks.mainFilters}
                onFilterChange={hooks.setMainFilters}
              />
            </div>
          ) : null}
        </div>
        {!hooks.dataReady ? null : recordingsEmpty ? (
          <EmptyState
            className="scp-cr-empty"
            illustration={<RecordIcon className="empty-icon" />}
            title="No recordings found"
            description={
              hasSearchOrFilter
                ? 'No recordings match your search.'
                : "You don't have any recent recordings on record yet."
            }
          />
        ) : (
          <DataTable<RecordingTableRow>
            rows={pageRows}
            columns={columns}
            actionsLabel="Actions"
            renderActions={renderRecording((call) => (
              <CallRecordingActionsCell
                call={call}
                onDownload={hooks.onDownload}
                onRequestDelete={hooks.setPendingDelete}
              />
            ))}
            sortColumnId={sortColumnId ?? undefined}
            sortDirection={sortColumnId ? sortDirection : undefined}
            onSort={handleSort}
            pagination={{
              currentPage: pageNumber,
              totalPages,
              pageSize,
              onPageChange: setCurrentPage,
              onPageSizeChange,
            }}
          />
        )}
      </div>

      <Modal
        open={hooks.pendingDelete !== null}
        onClose={() => hooks.setPendingDelete(null)}
        size="sm"
        variant="confirmation"
        className="unsaved-changes-modal"
        title="Delete recording?"
        icon={<Delete2Icon width={16} height={16} aria-hidden="true" />}
        iconTone="danger"
        description="This removes the call log recording from the list. This action cannot be undone."
        primaryLabel="Delete"
        secondaryLabel="Back"
        primaryDestructive
        onPrimaryClick={() => {
          void hooks.confirmDelete()
        }}
        onSecondaryClick={() => hooks.setPendingDelete(null)}
      />
    </div>
  )
}

export default CallRecording
